import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ResponseListsService } from './response-lists.service';
import { InputType, ResponseListColumn, ResponseListsWithStatistics } from '../../campaign/campaign.model';

/**
 * Display labels for stored visitor property keys (e.g. first_name), taken
 * from the widget input fields that captured them: every response list column
 * carries the stored field key and the label the input had in the widget
 * editor. Built-in field types fall back to their standard localized names;
 * consumers show the raw key when a property has no label here.
 */
@Injectable({ providedIn: 'root' })
export class PropertyLabelService {
  private readonly responseListsService = inject(ResponseListsService);
  private readonly translateService = inject(TranslateService);

  private labelMap$: Observable<Map<string, string>> | null = null;

  /** Property key to display label, fetched once per session and shared. */
  public getLabelMap(): Observable<Map<string, string>> {
    if (!this.labelMap$) {
      this.labelMap$ = this.responseListsService.getResponseListsWithStatistics().pipe(
        map((lists) => this.buildMap(lists ?? [])),
        catchError(() => of(new Map<string, string>())),
        shareReplay(1),
      );
    }
    return this.labelMap$;
  }

  private buildMap(lists: ResponseListsWithStatistics[]): Map<string, string> {
    const labels = new Map<string, string>();
    for (const list of lists) {
      for (const column of list.columns ?? []) {
        if (!column.key || labels.has(column.key)) {
          continue;
        }
        const label = column.name?.trim() || this.standardLabel(column);
        if (label) {
          labels.set(column.key, label);
        }
      }
    }
    // The built-in field keys always get their standard names, also when no
    // response list (or none with a custom label) contains them.
    const standardKeys: [string, string][] = [
      ['email', 'response-lists.columns.email'],
      ['name', 'response-lists.columns.name'],
      ['first_name', 'response-lists.columns.firstname'],
      ['last_name', 'response-lists.columns.lastname'],
      ['phone', 'response-lists.columns.phone'],
    ];
    for (const [key, translationKey] of standardKeys) {
      if (!labels.has(key)) {
        labels.set(key, this.translateService.instant(translationKey));
      }
    }
    return labels;
  }

  /** Standard field types keep their localized names when the input had no custom label. */
  private standardLabel(column: ResponseListColumn): string {
    switch (column.type) {
      case InputType.Email:
        return this.translateService.instant('response-lists.columns.email');
      case InputType.FullName:
        return this.translateService.instant('response-lists.columns.name');
      case InputType.FirstName:
        return this.translateService.instant('response-lists.columns.firstname');
      case InputType.LastName:
        return this.translateService.instant('response-lists.columns.lastname');
      case InputType.Phone:
        return this.translateService.instant('response-lists.columns.phone');
      default:
        return '';
    }
  }
}
